import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import Notification from "../../components/Notification";
import { formatToDayMonthYear } from "../../utils/TextUtils";
import { allowedToView } from "../../utils/PermissionUtils";

function Missions() {
    const [data, setData] = useState({
        missions: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    });
    const [missionTypes, setMissionTypes] = useState([]);
    const [printing, setPrinting] = useState(false);
    const [terms, setTerms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const location = useLocation();

    const [filters, setFilters] = useState({
        search: "",
        term_id: "",
        mission_type_id: "",
        page: 1,
        per_page: 15
    });

    const [pendingSearch, setPendingSearch] = useState("");

    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedMission, setSelectedMission] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });

    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    const isAdmin = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator', 'treasurer']);
    const navigate = useNavigate();

    // Fetch mission types and terms once on mount
    useEffect(() => {
        const fetchMetaData = async () => {
            try {
                const token = localStorage.getItem("access_token");

                const [typesRes, termsRes] = await Promise.all([
                    fetch(`${API_URL}/mission-types`, {
                        headers: { "Authorization": `Bearer ${token}` }
                    }),
                    fetch(`${API_URL}/terms`, {
                        headers: { "Authorization": `Bearer ${token}` }
                    })
                ]);

                if (typesRes.ok && termsRes.ok) {
                    const typesData = await typesRes.json();
                    const termsData = await termsRes.json();

                    setMissionTypes(typesData.data || typesData);
                    setTerms(termsData.data || termsData);
                } else {
                    throw new Error("Failed to load metadata");
                }
            } catch (error) {
                console.error("Metadata Fetch Error:", error);
            }
        };

        fetchMetaData();
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await fetch(`${API_URL}/missions?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                const missionsData = await response.json();
                setData({
                    missions: missionsData.data,
                    current_page: missionsData.current_page,
                    last_page: missionsData.last_page,
                    per_page: missionsData.per_page,
                    total: missionsData.total
                });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch missions");
            }
        } catch (error) {
            setError("An error occurred while fetching data.");
            console.error("Fetch Data Error:", error);
        } finally {
            setLoading(false);
        }
    }, [filters, navigate, location]);

    useEffect(() => {
        fetchData();
    }, [filters.term_id, filters.mission_type_id, filters.page, filters.per_page, fetchData]);

    const isPastMission = (startDate) => {
        if (!startDate) return false;
        const today = new Date();
        const missionDate = new Date(startDate);
        return missionDate <= today;
    };

    const toggleDropdown = (index) => {
        setIsDropdownOpen(isDropdownOpen === index ? null : index);
    };

    const handleDeleteClick = (mission) => {
        setSelectedMission(mission);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteMission = async () => {
        if (!selectedMission) return;

        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/missions/${selectedMission.id}`, {
                method: 'DELETE',
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                setNotification({ type: "success", message: 'Mission deleted successfully' });
                fetchData();
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Mission not found.' });
            } else {
                setNotification({ type: "danger", message: 'There was an error.' });
            }
        } catch (error) {
            setNotification({ type: 'danger', message: 'An error occurred while deleting the mission.' });
            console.error("Delete Error:", error);
        } finally {
            setIsConfirmDialogOpen(false);
            setSelectedMission(null);
        }
    };

    const cancelDeleteMission = () => {
        setIsConfirmDialogOpen(false);
        setSelectedMission(null);
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearchInputChange = (e) => {
        setPendingSearch(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters(prev => ({ ...prev, search: pendingSearch, page: 1 }));
    };

    const handleClearSearch = () => {
        setPendingSearch("");
        setFilters(prev => ({
            ...prev,
            search: "",
            page: 1
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value);
        setFilters(prev => ({
            ...prev,
            per_page: perPage,
            page: 1
        }));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null && !event.target.closest('.dropdown.is-active')) {
                setIsDropdownOpen(null);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [isDropdownOpen]);

    const handlePrintSchedule = async () => {
        if (!filters.term_id) {
            setNotification({ type: "warning", message: "Please select a term before printing." });
            return;
        }

        // Find the selected term object
        const selectedTerm = terms.find(t => t.id === parseInt(filters.term_id));
        if (!selectedTerm) {
            setNotification({ type: "danger", message: "Selected term not found." });
            return;
        }

        setPrinting(true);
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/missions/export-pdf?term_id=${filters.term_id}`, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/pdf"
                }
            });

            if (!response.ok) {
                if (response.status === 401) return navigate("/logout");
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to generate PDF");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // Build filename from term name (slugified)
            const termNameSlug = selectedTerm.name.toLowerCase().replace(/\s+/g, '_');
            const filename = `mission_schedule_${termNameSlug}.pdf`;
            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setNotification({ type: "success", message: "PDF generated successfully!" });
        } catch (error) {
            console.error("Print error:", error);
            setNotification({ type: "danger", message: error.message || "Failed to generate PDF." });
        } finally {
            setPrinting(false);
        }
    };

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Missions</p>
                        </div>
                    </div>
                    <div className="level-right">
                        <p className="level-item">
                            <button
                                className="button is-info"
                                onClick={handlePrintSchedule}
                                disabled={printing}
                            >
                                {printing ? (
                                    <>
                                        <span className="icon is-small">
                                            <i className="fas fa-spinner fa-spin"></i>
                                        </span>
                                        <span>Printing...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="icon is-small">
                                            <i className="fas fa-print"></i>
                                        </span>
                                        <span>Print Schedule (PDF)</span>
                                    </>
                                )}
                            </button>
                        </p>
                        
                        {canEdit(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSearchSubmit}>
                    <div className="columns">
                        <div className="column">
                            <div className="field">
                                <label className="label">Search</label>
                                <div className="field has-addons">
                                    <div className="control is-expanded">
                                        <input
                                            className="input"
                                            type="text"
                                            name="search"
                                            placeholder="Search by name, school, or type"
                                            value={pendingSearch}
                                            onChange={handleSearchInputChange}
                                        />
                                    </div>
                                    <div className="control">
                                        <button
                                            type="button"
                                            className="button is-light"
                                            onClick={handleClearSearch}
                                            disabled={!pendingSearch && !filters.search}
                                        >
                                            <span className="icon">
                                                <i className="fas fa-times"></i>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="column is-narrow">
                            <div className="field">
                                <label className="label">&nbsp;</label>
                                <div className="field is-grouped">
                                    <div className="control">
                                        <button
                                            type="submit"
                                            className="button primary-outline-button"
                                        >
                                            Search
                                        </button>
                                    </div>
                                    <div className="control">
                                        <button
                                            type="button"
                                            className="button is-light"
                                            onClick={handleClearSearch}
                                            disabled={!pendingSearch && !filters.search}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>


                <div className="columns">
                    <div className="column">
                        <div className="field">
                            <label className="label">Term</label>
                            <div className="control select is-fullwidth">
                                <select name="term_id" value={filters.term_id} onChange={handleFilterChange}>
                                    <option value="">All Terms</option>
                                    {terms.map(term => (
                                        <option key={term.id} value={term.id}>{term.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="column">
                        <div className="field">
                            <label className="label">Mission Type</label>
                            <div className="control select is-fullwidth">
                                <select name="mission_type_id" value={filters.mission_type_id} onChange={handleFilterChange}>
                                    <option value="">All Types</option>
                                    {missionTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="column">
                        <div className="field">
                            <label className="label">Items per page</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select value={filters.per_page} onChange={handlePerPageChange}>
                                        {[10, 15, 25, 50].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                {loading ? (
                    <div className="has-text-centered">
                        <p>Loading missions...</p>
                        <progress className="progress is-small is-primary" max="100">15%</progress>
                    </div>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table is-striped is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Status</th>
                                        <th>Leader</th>
                                        <th>Feedback</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.missions.length > 0 ? (
                                        data.missions.map((mission, index) => (
                                            <tr key={mission.id}>
                                                <td>{(data.current_page - 1) * data.per_page + index + 1}</td>
                                                <td>
                                                    <Link to={`${mission.id}/details`} className="default-color">
                                                        {mission?.school?.name} - {mission?.mission_type?.name}
                                                    </Link>
                                                </td>
                                                <td>
                                                    {mission.start_date && formatToDayMonthYear(mission.start_date)}
                                                    {mission.start_time && `, ${mission.start_time}`}
                                                </td>
                                                <td>
                                                    {mission.end_date && (
                                                        mission.start_date === mission.end_date
                                                            ? mission.end_time && `, ${mission.end_time}`
                                                            : `${formatToDayMonthYear(mission.end_date)}${mission.end_time ? `, ${mission.end_time}` : ''}`
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`tag ${mission.status === 'Active' ? 'is-success' : 'is-danger'}`}>
                                                        {mission.status || "N/A"}
                                                    </span>
                                                </td>
                                                <td>
                                                    {mission?.mission_leader ? (
                                                        isAdmin(userRole) ? (
                                                            <Link
                                                                to={`/dashboard/members/${mission.mission_leader.id}/details`}
                                                                className="default-color"
                                                            >
                                                                {mission.mission_leader.first_name || ''} {mission.mission_leader.last_name || ''}
                                                            </Link>
                                                        ) : (
                                                            <span>
                                                                {mission.mission_leader.first_name || ''} {mission.mission_leader.last_name || ''}
                                                            </span>
                                                        )
                                                    ) : (
                                                        <span className="tag is-light">N/A</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {isPastMission(mission.start_date) ? (
                                                        mission.has_feedback ? (
                                                            <Link to={`/dashboard/missions/${mission.id}/feedback/details`}>
                                                                <span className="tag is-success">Received</span>
                                                            </Link>
                                                        ) : (
                                                            <span className="tag is-warning">Pending</span>
                                                        )
                                                    ) : (
                                                        <span className="tag is-light">N/A</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                <Link to={`${mission.id}/details`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-eye"></i>
                                                                    </span>
                                                                    <span>View</span>
                                                                </Link>
                                                                {canEdit(userRole) && (
                                                                    <>
                                                                        <Link to={`${mission.id}/update`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-edit"></i>
                                                                            </span>
                                                                            <span>Edit</span>
                                                                        </Link>
                                                                        <button className="dropdown-item" onClick={() => handleDeleteClick(mission)}>
                                                                            <span className="icon">
                                                                                <i className="fas fa-trash-alt"></i>
                                                                            </span>
                                                                            <span>Delete</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="has-text-centered">No missions found matching your criteria</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {data.total > 0 && (
                            <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                                <button
                                    className="pagination-previous"
                                    onClick={() => handlePageChange(data.current_page - 1)}
                                    disabled={data.current_page === 1}
                                >
                                    Previous
                                </button>
                                <button
                                    className="pagination-next"
                                    onClick={() => handlePageChange(data.current_page + 1)}
                                    disabled={data.current_page === data.last_page}
                                >
                                    Next
                                </button>
                                <ul className="pagination-list">
                                    {Array.from({ length: data.last_page }, (_, i) => i + 1).map(page => (
                                        <li key={page}>
                                            <button
                                                className={`pagination-link ${data.current_page === page ? 'is-current' : ''}`}
                                                aria-label={`Go to page ${page}`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        )}
                    </>
                )}
                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-danger"
                    title="Delete Confirmation"
                    message={`Are you sure you want to delete the mission "${selectedMission?.name}"?`}
                    onConfirm={confirmDeleteMission}
                    onCancel={cancelDeleteMission}
                    isLoading={false}
                />
            </main>
        </div>
    );
}

export default Missions;
